
import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import TelemetryTabContent from './TelemetryTabContent';
import DeviceStatusCards from '@/components/telemetry/DeviceStatusCards';
import DeviceSummaryCharts from '@/components/telemetry/DeviceSummaryCharts';
import { Device } from '@/components/device-health/gis-map/types';

interface TelemetryTabsProps {
  activeTab: string;
  setActiveTab: (value: string) => void;
  chartData: any[];
  latestData: any;
  selectedSection: string | null;
  onCardClick: (section: string) => void;
  telemetryType: 'energy' | 'gas' | 'water';
  gatewayStatusRef: React.RefObject<HTMLDivElement>;
  performanceMetricsRef: React.RefObject<HTMLDivElement>;
  deviceId?: string;
  deviceData?: Device | null;
}

const TelemetryTabs: React.FC<TelemetryTabsProps> = ({
  activeTab,
  setActiveTab,
  chartData,
  latestData,
  selectedSection,
  onCardClick,
  telemetryType,
  gatewayStatusRef,
  performanceMetricsRef,
  deviceId,
  deviceData
}) => {
  // For a specific device, change the tab labels
  const getTabLabels = () => {
    if (deviceId) {
      return {
        all: 'Overview',
        smartMeters: 'Readings',
        gateways: 'Network'
      };
    }
    
    return {
      all: 'All',
      smartMeters: 'Meters',
      gateways: 'Gateways'
    };
  };
  
  const tabLabels = getTabLabels();

  return (
    <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
      <div className="flex-none">
        <TabsList className="grid w-full grid-cols-3 mb-2">
          <TabsTrigger value="all" className="text-xs py-1">{tabLabels.all}</TabsTrigger>
          <TabsTrigger value="smartMeters" className="text-xs py-1">{tabLabels.smartMeters}</TabsTrigger>
          <TabsTrigger value="gateways" className="text-xs py-1">{tabLabels.gateways}</TabsTrigger>
        </TabsList>
      </div>
      
      <div className="flex-1 overflow-hidden">
        <TabsContent value="all" className="h-full overflow-auto m-0">
          <TelemetryTabContent 
            chartData={chartData}
            latestData={latestData}
            selectedSection={selectedSection}
            onCardClick={onCardClick}
            telemetryType={telemetryType}
            gatewayStatusRef={gatewayStatusRef}
            performanceMetricsRef={performanceMetricsRef}
            deviceId={deviceId}
            deviceData={deviceData}
          />
        </TabsContent>
        
        <TabsContent value="smartMeters" className="h-full overflow-auto m-0">
          <div className="space-y-4">
            {/* Smart Meter Summary Cards */}
            <DeviceStatusCards 
              data={latestData.smartMeters} 
              type="smartMeters" 
            />
            
            {/* Smart Meter Charts */}
            <DeviceSummaryCharts 
              chartData={chartData} 
              telemetryType={telemetryType} 
              deviceType="smartMeters" 
            />
          </div>
        </TabsContent>
        
        <TabsContent value="gateways" className="h-full overflow-auto m-0">
          <div className="space-y-4">
            {/* Gateway Summary Cards */}
            <DeviceStatusCards 
              data={{
                ...latestData.gateways,
                dataTransferred: latestData.gateways.dataTransferred
              }} 
              type="gateways" 
            />
            
            {/* Gateway Charts */}
            <DeviceSummaryCharts 
              chartData={chartData} 
              telemetryType={telemetryType} 
              deviceType="gateways" 
            />
          </div>
        </TabsContent>
      </div>
    </Tabs>
  );
};

export default TelemetryTabs;
